import { useEffect, useState } from 'react';
import { useAppDispatch } from 'hooks';

import { getFeatureFlag } from '~main/actions/login';

export interface UseFeatureFlagResult {
  /**
   * Whether the flag is enabled. Always `false` while `isResolved` is `false` — callers that need
   * the "off" default while the request is in flight can read this directly, but anything gating
   * user-visible content should check `isResolved` first (see that field's doc for why).
   */
  isEnabled: boolean;
  /**
   * `true` once the flag request has settled (success or failure). Callers gating render output
   * MUST wait for this before deciding to show anything — rendering as soon as `isEnabled` flips
   * true is safe, but rendering *before* `isResolved` risks a flash of stale/default state (e.g.
   * a status badge appearing briefly on every load, then disappearing once the real value arrives).
   */
  isResolved: boolean;
}

// The resolved result is stored together with the flag it belongs to, so a result can never be
// read as belonging to a different flag than the one currently requested.
interface FlagState {
  flag: string;
  isEnabled: boolean;
  isResolved: boolean;
}

/**
 * The `GET /feature-flags/:flag` body. Declared here rather than on the thunk because
 * `getFeatureFlag` comes out of the generic `createThunks` factory (`~libs/reduxUtils`), whose
 * accumulator is `Record<string, any>` — so none of the thunks it produces carries a response type
 * and there is nothing to import. Typing the factory itself would mean giving every generated thunk
 * a declared shape, which is a far wider change than this hook needs.
 *
 * Every field is optional because the controller's failure path (`400`) returns `{ message }` alone
 * — no `success`, no `data` — so neither can be assumed present. `data` is the flag value itself
 * (the controller assigns it straight from `isEnabled`), which is why the check below is
 * `success && data` rather than `success` alone.
 */
interface FeatureFlagResponse {
  success?: boolean;
  message?: string;
  data?: boolean;
}

/**
 * Resolves a single GitLab feature flag via the existing `getFeatureFlag` thunk
 * (`~main/actions/login`), consolidating the shape that `Header.tsx` and `QuickMenu.tsx` each
 * hand-roll locally with their own `featureFlagStatus` state + `isFeatureEnabled` helper.
 *
 * Re-resolves whenever `flag` changes. Returns `isEnabled: false` until resolved so consumers
 * have a safe default, but see `isResolved` for why that default must not be rendered directly.
 */
const useFeatureFlag = (flag: string): UseFeatureFlagResult => {
  const dispatch = useAppDispatch();
  const [state, setState] = useState<FlagState>({ flag, isEnabled: false, isResolved: false });

  // Reset during render, not in the effect below. An effect-based reset runs *after* the first
  // render for the new flag has already returned, so that render would publish the PREVIOUS flag's
  // `isResolved: true` — and a caller gating rendered output on `isResolved` would act on it.
  // A render-phase update makes React re-run this component immediately, discarding that pass
  // before children ever see it. Terminates because `state.flag === flag` afterwards.
  if (state.flag !== flag) {
    setState({ flag, isEnabled: false, isResolved: false });
  }

  useEffect(() => {
    // Invalidates the in-flight request on unmount AND whenever `flag` changes, so a response that
    // arrives after the flag has moved on is dropped rather than published against the new flag.
    let isCurrent = true;

    const checkFlag = async () => {
      const { payload } = await dispatch(getFeatureFlag({ flag }));
      if (!isCurrent) return;

      const { success, data }: FeatureFlagResponse = payload ?? {};
      setState({ flag, isEnabled: !!(success && data), isResolved: true });
    };

    checkFlag();

    return () => {
      isCurrent = false;
    };
  }, [dispatch, flag]);

  return { isEnabled: state.isEnabled, isResolved: state.isResolved };
};

export default useFeatureFlag;
